import { useState, useRef, useEffect, type JSX } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { modalClasses } from '../modal.classes';
import { useModalBodySectionedScrollStyles } from './modalBody.styles';
import { BaseProps, SectionedScrollProps } from './modalBody';

/**
 * Defines the scrolling behavior within a sectioned layout:
 * - 'inner': Scrolling occurs inside the scrollable section when there is enough space.
 * - 'outer': Scrolling occurs outside the scrollable section, within the modal body, when there is not enough space.
 */
export type SectionedScrollBehavior = 'inner' | 'outer';

type ModalBodySectionedScrollProps = Omit<SectionedScrollProps, 'scrollType'> &
  BaseProps;

/**
 * The scrollable content area of the Modal.
 * The modal body contains a fixed section and a scrollable section.
 * If there is enough space, only the scrollable section can be scrolled.
 * If the modal body height is too small, the scrollable section becomes fixed, and the entire modal body scrolls instead.
 */
export const ModalBodySectionedScroll = ({
  fixedSection,
  scrollableSection,
  styles,
}: ModalBodySectionedScrollProps): JSX.Element => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const fixedSectionRef = useRef<HTMLDivElement>(null);
  const [scrollBehavior, setScrollBehavior] =
    useState<SectionedScrollBehavior>('inner');
  const css = useModalBodySectionedScrollStyles(scrollBehavior);

  useEffect(() => {
    const updateScrollBehavior = () => {
      if (!modalBodyRef.current || !fixedSectionRef.current) return;

      const offset = 100;
      const modalBodyHeight = modalBodyRef.current.clientHeight;
      const fixedSectionHeight = fixedSectionRef.current.clientHeight;
      const remainingHeight = modalBodyHeight - (fixedSectionHeight + offset);

      setScrollBehavior(remainingHeight <= 0 ? 'outer' : 'inner');
    };

    updateScrollBehavior();
    window.addEventListener('resize', updateScrollBehavior);
    return () => window.removeEventListener('resize', updateScrollBehavior);
  }, []);

  return (
    <Stack
      ref={modalBodyRef}
      className={modalClasses.body}
      style={styles}
      css={css.body}
    >
      <Box ref={fixedSectionRef} css={css.fixedSection}>
        {fixedSection}
      </Box>
      <Box css={css.scrollableSection}>{scrollableSection}</Box>
    </Stack>
  );
};
